import { type SkuId, type StoreName } from '@bluebrick/discounts-service-module'
import { type DateTime } from 'luxon'
import { z } from 'zod'
import { isEventCancellationError, stripName } from '@/common/utils'
import { useApi } from '@/features/api/useAPI'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { type SaleDaysSummaryData } from '@/features/saleDaysSummary/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { eventDataSchema, recommendationsSchema, saleDaysSummarySchema, storeDataSchema, storesSchemaLenient } from '@/schema'
import { type Recommendations, type EventData, type StoreData, type Store } from '@/types'

export async function fetchAll(): Promise<Store[] | undefined> {
	const { notifyError } = useNotificationsStore()
	const { trackException, trackEvent } = useTelemetry()
	const { apiGet } = useApi()

	try {
		const { data } = await apiGet<Store[]>(`/events/v2/stores`)

		trackEvent('app', 'Loaded stores', {
			data: JSON.stringify(data)
		})

		return storesSchemaLenient.parse(data)
	} catch (e) {
		trackException(e as Error, 'Failed to load stores.')

		notifyError('Failed to load stores.')
	}
}

export async function fetchEventsWithCooldowns(
	storeName: StoreName,
	startDate: DateTime,
	endDate: DateTime,
	skuIds?: SkuId[],
	abortSignal?: AbortSignal
): Promise<StoreData | undefined> {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		const { data } = await apiPost<StoreData>(
			`/events/store/${storeName}/events`,
			{
				startDate: startDate.toUTC().toISO(),
				endDate: endDate.toUTC().toISO(),
				skuIds
			},
			{ signal: abortSignal, fetchOptions: { canRetry: true } }
		)

		return storeDataSchema.parse(data)
	} catch (e) {
		if (isEventCancellationError(e, abortSignal)) {
			return
		}

		trackException(e as Error, `Failed to load events for ${storeName}.`, {
			startDate: startDate.toISO() ?? undefined,
			endDate: endDate.toISO() ?? undefined
		})

		notifyError(`Failed to load events for ${storeName}.`)
	}
}

export async function fetchOpportunities(
	storeName: StoreName,
	events: EventData[],
	cooldowns: EventData[],
	startDate: Date | undefined,
	endDate: Date,
	skuIds: SkuId[],
	abortSignal?: AbortSignal
): Promise<EventData[] | undefined> {
	if (events.length === 0 && skuIds.length === 0) {
		return
	}

	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		const { data } = await apiPost<EventData[]>(
			`/events/store/${storeName}/opportunities`,
			{
				// #event-names-vs-firewall
				events: events.length > 0 ? events.map(stripName) : undefined,
				cooldowns: cooldowns?.length ? cooldowns.map(stripName) : undefined,
				startDate,
				endDate,
				skuIds
			},
			{ signal: abortSignal, fetchOptions: { canRetry: true } }
		)

		return z.array(eventDataSchema).parse(data)
	} catch (e) {
		if (isEventCancellationError(e, abortSignal)) {
			return
		}

		trackException(e as Error, `Failed to load opportunities for ${storeName}.`)

		notifyError(`Failed to load opportunities for ${storeName}.`)
	}
}

export async function fetchRecommendations(
	storeName: StoreName,
	events: EventData[],
	abortSignal?: AbortSignal
): Promise<Recommendations | undefined> {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		const { data } = await apiPost<Recommendations>(
			`/events/store/${storeName}/recommendations`,
			{
				// #event-names-vs-firewall
				events: events.map(stripName)
			},
			{ signal: abortSignal, fetchOptions: { canRetry: true } }
		)

		return recommendationsSchema.parse(data)
	} catch (e) {
		if (isEventCancellationError(e, abortSignal)) {
			return
		}

		trackException(e as Error, `Failed to load recommendations for ${storeName}.`)

		notifyError(`Failed to load recommendations for ${storeName}.`)
	}
}

export async function fetchSaleDaysSummary(
	storeNames: StoreName[],
	startDate: DateTime<true>,
	endDate: DateTime<true>,
	skuIdsByStore: Map<StoreName, SkuId[]>,
	abortSignal?: AbortSignal
): Promise<SaleDaysSummaryData | undefined> {
	const { notifyError } = useNotificationsStore()
	const { trackException } = useTelemetry()
	const { apiPost } = useApi()

	try {
		const { data } = await apiPost<SaleDaysSummaryData>(
			`/events/saleDaysSummary`,
			{
				storeNames,
				startDate: startDate.toISO(),
				endDate: endDate.toISO(),
				skuIdsByStore: Object.fromEntries(skuIdsByStore)
			},
			{ signal: abortSignal, fetchOptions: { canRetry: true } }
		)

		return saleDaysSummarySchema.parse(data)
	} catch (e) {
		if (isEventCancellationError(e, abortSignal)) {
			return
		}

		trackException(e, `Failed to load sale days summary for stores:${storeNames}, start date: ${startDate}, end date: ${endDate}`)

		notifyError('Failed to load sale days summary')
	}
}
